import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { ModalIntegracoes } from './ModalIntegracoes';
import { Integracao } from '../../../../../shared/services/api/c4-model/SistemasService';
import { Skeleton } from '@mui/material';

type Row = {
  id: string;
  sistemaId: string;
  sistema: string;
  tipo: string;
  descricao: string;
};

type Props = {
  initialRows?: Integracao[];
  currentSystemId?: string; // ID do sistema atual
  onIntegracoesChange?: (integracoes: Integracao[]) => void;
  isLoading?: boolean;
};

const convertToRows = (integracoes: Integracao[]): Row[] => {
  return integracoes.map((item) => ({
    id: item.id,
    sistemaId: item.sistemaIntegracaoId,
    // sistemaIntegracao can be a C4Sistemas object or a string; normalize to nome when possible
    sistema:
      typeof item.sistemaIntegracao === 'string'
        ? item.sistemaIntegracao
        : item.sistemaIntegracao?.nome || 'Sistema não identificado',
    tipo: item.tipoIntegracao,
    descricao: item.descricao,
  }));
};

const convertToIntegracoes = (
  rows: Row[],
  savedValues?: any,
  currentSystemId?: string,
  sourceIntegracoes?: Integracao[]
): Integracao[] => {
  return rows.map((row) => {
    // Se savedValues representa uma única integração editada/criada, use-a
    const savedIntegracao = savedValues && savedValues.id === row.id ? savedValues : null;

    // Encontrar a integração original nas sourceIntegracoes para preservar objeto completo
    const original = sourceIntegracoes?.find((i) => i.id === row.id) || null;

    return {
      id: row.id,
      // sistemaId é o sistema dono da integração (preenchido pelo formulário via prop)
      sistemaId: currentSystemId || '',
      // id do sistema integrado (string)
      sistemaIntegracaoId: row.sistemaId || '',
      // tipo no shape esperado pelo service
      tipoIntegracao: row.tipo,
      descricao: row.descricao,
      // Prefer savedIntegracao.sistemaIntegracao, senão manter o original, senão null
      sistemaIntegracao: savedIntegracao?.sistemaIntegracao || original?.sistemaIntegracao || null,
      // Prefer saved acessoContainers, senão usar original, senão array vazio
      acessoContainers: savedIntegracao?.acessoContainers || original?.acessoContainers || [],
    } as Integracao;
  });
};

export const ListaIntegracoes: React.FC<Props> = ({
  initialRows = [],
  currentSystemId,
  onIntegracoesChange,
  isLoading = false,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingRow, setEditingRow] = useState<Row | null>(null);

  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      // When no initial rows are provided, start with empty array
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setEditingRow(row);
    setOpenModal(true);
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);
    if (onIntegracoesChange) {
      onIntegracoesChange(convertToIntegracoes(newRows, undefined, currentSystemId, initialRows));
    }
  };

  const handleOpen = () => {
    // clear editing when opening for a new entry
    setEditingRow(null);
    setOpenModal(true);
  };
  const handleClose = () => setOpenModal(false);

  const handleSave = (values: any) => {
    let newRows: Row[] = [];
    if (values.id) {
      // Editando integração existente
      newRows = rows.map((r) =>
        r.id === values.id
          ? {
            ...r,
            sistemaId: values.sistemaIntegracaoId || values.sistemaId || r.sistemaId,
            sistema: values.sistema || r.sistema,
            tipo: values.tipo || r.tipo,
            descricao: values.descricao || r.descricao,
          }
          : r
      );

      // Update integracoes com dados estruturados
      if (onIntegracoesChange) {
        const updatedIntegracoes = convertToIntegracoes(newRows, values, currentSystemId, initialRows);
        onIntegracoesChange(updatedIntegracoes);
      }
    } else {
      // Criando nova integração
      const newRow: Row = {
        id: String(Date.now()),
        sistemaId: values.sistemaIntegracaoId || values.sistemaId || '',
        sistema: values.sistema || 'Sistema não identificado',
        tipo: values.tipo || 'SAAS',
        descricao: values.descricao || '',
      };
      newRows = [newRow, ...rows];

      // Criar nova integração com dados estruturados
      if (onIntegracoesChange) {
        // Ajustar o values para incluir o ID da nova row e garantir sistemaIntegracaoId
        const valuesWithId = { ...values, id: newRow.id, sistemaIntegracaoId: values.sistemaIntegracaoId || values.sistemaId || '' };
        const newIntegracoes = convertToIntegracoes(newRows, valuesWithId, currentSystemId, initialRows);
        onIntegracoesChange(newIntegracoes);
      }
    }
    setRows(newRows);
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={4}>
      {isLoading ? (
        <Skeleton variant="rectangular" width={80} height={36} />
      ) : (
        <Button
          variant="contained"
          color="primary"
          sx={{ width: '80px' }}
          onClick={handleOpen}
          startIcon={<AddIcon />}
        >
          Novo
        </Button>
      )}
      <ModalIntegracoes
        open={openModal}
        onClose={() => {
          handleClose();
          setEditingRow(null);
        }}
        onSave={(v: any) => {
          handleSave(v);
          handleClose();
          setEditingRow(null);
        }}
        currentSystemId={currentSystemId}
        initialValues={
          editingRow
            ? {
              id: editingRow.id,
              sistemaId: editingRow.sistemaId,
              sistema: editingRow.sistema,
              tipo: editingRow.tipo,
              descricao: editingRow.descricao,
              acessoContainers:
                initialRows.find((i) => i.id === editingRow.id)
                  ?.acessoContainers || [],
              // Extrair acessoComponentes dos acessoContainers para compatibilidade com o modal
              acessoComponentes: (() => {
                const integracao = initialRows.find(
                  (i) => i.id === editingRow.id
                );
                if (
                  integracao &&
                  integracao.acessoContainers &&
                  integracao.acessoContainers.length > 0
                ) {
                  // Combinar todos os acessoComponentes de todos os acessoContainers
                  return integracao.acessoContainers.flatMap(
                    (container) => container.acessoComponentes || []
                  );
                }
                return [];
              })(),
            }
            : undefined
        }
      />
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Sistema</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Descrição</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3].map((n) => (
                  <TableRow key={n}>
                    <TableCell>
                      <Skeleton variant="rectangular" width={80} height={30} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={150} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={100} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={200} />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{ py: 4, color: 'text.secondary' }}
                >
                  Nenhuma integração cadastrada
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(row.id)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{row.sistema}</TableCell>
                  <TableCell>{row.tipo}</TableCell>
                  <TableCell>{row.descricao}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
